import React, { useEffect, useState } from "react";

import { api, formatApiError } from "../lib/api";

// Phase 1 (2026-10-15) · "Sign-ups by post / campaign / channel" panel.
// Reads /api/brand-os/signups/by-source and renders a compact 3-column
// snapshot. Shown on the Insights page under the attribution cards.
//
// The data is additive to the existing Meta attribution rollups: here
// each row is an on-site sign-up tagged with the UTMs the public form
// captured on landing. The point is to show which post or campaign
// drove the sign-up without waiting on Meta's attribution window.

export default function SignupsBySourcePanel({ product = null, days = 30 }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(days);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set("days", String(range));
        if (product) params.set("product", product);
        const r = await api.get(`/signups/by-source?${params.toString()}`);
        if (alive) {
          setData(r.data);
          setErr(null);
        }
      } catch (e) {
        if (alive) setErr(formatApiError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [range, product]);

  const totals = data?.totals || {};
  const sections = [
    { title: "By channel", rows: data?.by_channel || [], subKey: "sources" },
    { title: "By campaign", rows: data?.by_campaign || [], subKey: "sources" },
    { title: "By post", rows: data?.by_post || [], subKey: "campaigns" },
  ];

  return (
    <div className="bo-card" data-testid="signups-by-source" style={{ padding: 16, marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <div>
          <div className="bo-eyebrow">Phase 1 · Attribution</div>
          <h3 style={{ margin: "2px 0 0 0", fontFamily: "var(--bo-serif)", fontSize: 18 }}>
            Sign-ups by post / campaign / channel
          </h3>
          <p style={{ color: "var(--bo-muted)", margin: "4px 0 0 0", fontSize: 13 }}>
            Captured from UTM params on the public forms. Updates on every signup.
          </p>
        </div>
        <select
          data-testid="signups-range"
          value={range}
          onChange={(e) => setRange(Number(e.target.value))}
          className="bo-select"
          style={{ fontSize: 13, padding: "6px 10px" }}
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last year</option>
        </select>
      </div>

      {err && <div style={{ color: "var(--bo-danger)", fontSize: 13, marginTop: 10 }} data-testid="signups-error">{err}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 10, marginTop: 12 }}>
        <Metric label="Sign-ups" value={totals.signups} testId="signups-total" />
        <Metric label="With attribution" value={totals.with_attribution} testId="signups-with-attr" />
        <Metric label="No attribution" value={totals.unattributed} testId="signups-no-attr" />
        <Metric label="CAPI sent" value={totals.capi_sent} testId="signups-capi" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12, marginTop: 14 }}>
        {sections.map((sec) => (
          <SubTable key={sec.title} title={sec.title} rows={sec.rows} loading={loading} />
        ))}
      </div>
    </div>
  );
}

function Metric({ label, value, testId }) {
  return (
    <div data-testid={testId} style={{ padding: "8px 10px", background: "var(--bo-panel-soft, rgba(0,0,0,0.03))", borderRadius: 8 }}>
      <div style={{ fontSize: 11, letterSpacing: ".18em", textTransform: "uppercase", color: "var(--bo-muted)" }}>{label}</div>
      <div style={{ fontFamily: "var(--bo-serif)", fontSize: 22, color: "var(--bo-ink)" }}>{value ?? "—"}</div>
    </div>
  );
}

function SubTable({ title, rows, loading }) {
  return (
    <div data-testid={`signups-section-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="bo-eyebrow" style={{ marginBottom: 6 }}>{title}</div>
      <table className="bo-table" style={{ fontSize: 13, width: "100%" }}>
        <thead>
          <tr>
            <th style={{ textAlign: "left" }}>Key</th>
            <th style={{ textAlign: "right", width: 70 }}>Signups</th>
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr><td colSpan={2} style={{ color: "var(--bo-muted)" }}>Loading…</td></tr>
          )}
          {!loading && rows.length === 0 && (
            <tr><td colSpan={2} style={{ color: "var(--bo-muted)", fontStyle: "italic" }}>
              No tagged sign-ups in this window yet.
            </td></tr>
          )}
          {rows.map((r) => (
            <tr key={r.key}>
              <td style={{ padding: "4px 6px 4px 0", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 220, whiteSpace: "nowrap" }} title={r.key}>
                {r.key}
              </td>
              <td style={{ padding: "4px 0", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                {r.signups}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
